<template>
  <div class="history-field" :class="{ same: !field.changes && mode != 'clean' }">
    <div class="history-field-header">
      <i v-if="!field.changes && mode != 'clean'" class="far fa-check"></i>
      {{ field.name }}
      <span v-if="field.changes" class="badge rounded-pill history-pill">{{ field.changes }} {{ field.changes == 1 ? 'change' : 'changes' }}</span>
      <span v-else-if="field.formatting" class="badge rounded-pill history-pill muted" title="Only bold, italic or other formatting changed">Formatting only</span>
      <span v-else class="badge rounded-pill history-pill muted">No changes</span>
    </div>

    <template v-if="mode == 'clean'">
      <div class="history-split">
        <div class="history-rows">
          <div class="history-col-header">{{ fromLabel }}</div>
          <history-row v-for="(row, index) in cleanRows(field.fromLines)" :key="index" :row="row" />
        </div>
        <div class="history-rows">
          <div class="history-col-header">{{ toLabel }}</div>
          <history-row v-for="(row, index) in cleanRows(field.toLines)" :key="index" :row="row" />
        </div>
      </div>
    </template>

    <template v-else-if="field.changes">
      <div v-if="mode == 'inline'" class="history-rows">
        <history-row v-for="(row, index) in inlineRows" :key="index" :row="row" />
      </div>

      <div v-else class="history-split">
        <div class="history-rows">
          <div class="history-col-header">{{ fromLabel }}</div>
          <history-row v-for="(row, index) in splitRows.left" :key="index" :row="row" />
        </div>
        <div class="history-rows">
          <div class="history-col-header">{{ toLabel }}</div>
          <history-row v-for="(row, index) in splitRows.right" :key="index" :row="row" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import HistoryRow from './HistoryRow.vue'
import { collapseOps } from 'utils/story-history'

const wordRow = (type, line, parts) => ({
  type,
  marker: type == 'del' ? '−' : '+',
  level: line.level,
  parts
})

export default {
  components: {
    HistoryRow
  },

  props: {
    field: { type: Object, required: true },
    mode: { type: String, default: 'inline' },
    collapse: { type: Boolean, default: true },
    fromLabel: String,
    toLabel: String
  },

  computed: {
    ops () {
      return this.collapse ? collapseOps(this.field.ops) : this.field.ops
    },

    inlineRows () {
      return this.ops.reduce((rows, op) => {
        if (op.type == 'gap') {
          rows.push({ type: 'gap', text: `⋯ ${op.count} unchanged lines` })
        } else if (op.type == 'eq') {
          rows.push({ type: '', level: op.a.level, text: op.a.text })
        } else if (op.type == 'del') {
          rows.push(wordRow('del', op.a, [{ value: op.a.text, removed: true }]))
        } else if (op.type == 'ins') {
          rows.push(wordRow('ins', op.b, [{ value: op.b.text, added: true }]))
        } else {
          rows.push(wordRow('del', op.a, op.words.left))
          rows.push(wordRow('ins', op.b, op.words.right))
        }

        return rows
      }, [])
    },

    splitRows () {
      const left = []
      const right = []
      const blank = { type: 'blank', text: ' ' }

      this.ops.forEach((op) => {
        if (op.type == 'gap') {
          const gap = { type: 'gap', text: `⋯ ${op.count} unchanged` }
          left.push(gap)
          right.push(gap)
        } else if (op.type == 'eq') {
          left.push({ type: '', level: op.a.level, text: op.a.text })
          right.push({ type: '', level: op.b.level, text: op.b.text })
        } else if (op.type == 'del') {
          left.push(wordRow('del', op.a, [{ value: op.a.text, removed: true }]))
          right.push(blank)
        } else if (op.type == 'ins') {
          left.push(blank)
          right.push(wordRow('ins', op.b, [{ value: op.b.text, added: true }]))
        } else {
          left.push(wordRow('del', op.a, op.words.left))
          right.push(wordRow('ins', op.b, op.words.right))
        }
      })

      return { left, right }
    }
  },

  methods: {
    cleanRows (lines) {
      if (!lines.length) return [{ type: 'empty', text: 'Empty' }]

      return lines.map(line => ({ type: '', level: line.level, text: line.text }))
    }
  }
}
</script>
